import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import AndroidRounded from '@mui/icons-material/AndroidRounded';
import { tokens, fonts } from '../../theme';
import { Brand } from '../Navbar/Navbar';
import { DEV_EMAIL, DEV_SITE, PLAY_URL } from '../ui/constants';

const linkSx = {
  color: 'rgba(255,255,255,0.72)',
  textDecoration: 'none',
  fontSize: '0.95rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  borderRadius: '6px',
  '&:hover': { color: '#fff' },
  '&:focus-visible': { outline: `3px solid ${tokens.foil}`, outlineOffset: 3 },
  '& svg': { fontSize: 18 },
} as const;

const Col: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box component="nav" aria-label={title}>
    <Typography component="h2" sx={{ fontFamily: fonts.mono, fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', mb: 2 }}>
      {title}
    </Typography>
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.25 }}>
      {children}
    </Box>
  </Box>
);

const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  return (
    <Box component="footer" sx={{ bgcolor: tokens.night, color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ height: 4, background: tokens.holo, opacity: 0.8 }} />
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.6fr 1fr 1fr 1fr' },
            gap: { xs: 5, md: 6 },
          }}
        >
          <Box>
            <Brand light />
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.68)', maxWidth: 340, fontSize: '0.95rem' }}>
              An offline wallet for the cards and documents you carry. Made for Android phones and Wear OS watches by
              InnoApps.
            </Typography>
          </Box>
          <Col title="App">
            {[
              ['Features', '/features'],
              ['Security', '/security'],
              ['Document scanning', '/document-scanning'],
              ['Backup', '/cloud-backup'],
              ['Wear OS', '/wear-os'],
            ].map(([t, p]) => (
              <li key={p}>
                <Box component={RouterLink} to={p} sx={linkSx}>
                  {t}
                </Box>
              </li>
            ))}
          </Col>
          <Col title="InnoApps">
            <li>
              <Box component={RouterLink} to="/about" sx={linkSx}>
                About
              </Box>
            </li>
            <li>
              <Box component="a" href={DEV_SITE} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                <LanguageRounded /> innoapps.site
              </Box>
            </li>
            <li>
              <Box component="a" href={`mailto:${DEV_EMAIL}`} sx={{ ...linkSx, wordBreak: 'break-all' }}>
                <MailOutlineRounded /> {DEV_EMAIL}
              </Box>
            </li>
            <li>
              <Box component="a" href={PLAY_URL} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                <AndroidRounded /> Google Play
              </Box>
            </li>
          </Col>
          <Col title="Legal">
            <li>
              <Box component={RouterLink} to="/privacy-policy" sx={linkSx}>
                Privacy policy
              </Box>
            </li>
            <li>
              <Box component={RouterLink} to="/terms-and-conditions" sx={linkSx}>
                Terms and conditions
              </Box>
            </li>
          </Col>
        </Box>
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            pt: 3,
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.85rem',
          }}
        >
          <span>&copy; {year} InnoApps. All rights reserved.</span>
          <Box component="span" sx={{ fontFamily: fonts.mono }}>
            Android and Wear OS are trademarks of Google LLC.
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
